import { useId, useState, type FormEvent } from 'react';

interface ParentPanelProps {
  recoveryCode: string | null;
  onRecover: (code: string) => Promise<void>;
}

export function ParentPanel({ recoveryCode, onRecover }: ParentPanelProps) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const titleId = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await onRecover(code);
      setMessage('Family progress restored on this device.');
      setCode('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'That code did not work.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="parent-link" type="button" onClick={() => setOpen(true)}>
        For grown-ups
      </button>
      {open && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <section
            className="parent-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              className="modal-close"
              type="button"
              aria-label="Close parent area"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
            <p className="eyebrow">Parent area</p>
            <h2 id={titleId}>Keep this trail safe</h2>
            <p className="parent-copy">
              The kids’ trail on this device still uses its original family recovery code. The
              grown-up account is separate.
            </p>
            {recoveryCode && (
              <div className="recovery-card">
                <span>Your recovery code</span>
                <strong>{recoveryCode}</strong>
                <small>Keep a copy somewhere safe.</small>
              </div>
            )}
            <form onSubmit={(event) => void submit(event)}>
              <label htmlFor="recovery-code">Restore with a recovery code</label>
              <div className="recovery-form-row">
                <input
                  id="recovery-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  placeholder="MAPLE-OTTER-1234"
                  autoComplete="off"
                  required
                />
                <button type="submit" disabled={saving}>
                  {saving ? 'Checking…' : 'Restore'}
                </button>
              </div>
            </form>
            {message && <p className="parent-message">{message}</p>}
            <p className="privacy-note">
              Wild Trails does not ask for a child’s name, birthday, email, photo, or location.
            </p>
            <a className="account-portal-link" href="/account">
              Open grown-up account
            </a>
          </section>
        </div>
      )}
    </>
  );
}
